// FirstLoginFocusModal — opens the first time a user authenticates with no
// `tenants.settings.focus` set. Bound at the app shell level so it can
// surface on any post-login screen.
//
// Design call (from Phase D plan + CEO review): the marquee screen MUST
// NOT show an empty AI rail with apologetic copy on a fresh tenant. Forcing
// onboarding through a single short modal converts the cold-start moment
// from "the system is empty and shrugs" to "the system asks the right
// questions up front." The modal is dismissable (skip → focus stays empty,
// rail falls through to the popular-this-week vector), but the default
// affordance is "fill in 30 seconds and hit save."

import { ApiError, apiFetch } from '@/lib/api';
import { Drawer } from '@/primitives';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { type TenantFocus, TenantFocusForm } from './TenantFocusForm';

interface MeWithFocus {
  user_id: string;
  tenant_id: string;
  role: string;
  focus: TenantFocus | null;
}

export function FirstLoginFocusModal() {
  const qc = useQueryClient();
  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => apiFetch<MeWithFocus>('/v1/me'),
    // Don't poll; we re-query on mutation.
    staleTime: Number.POSITIVE_INFINITY,
  });

  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState<TenantFocus>({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Local "skip for this session" so a user can dismiss and re-open via
  // /settings/focus later. We never persist the dismissal — the next login
  // re-evaluates the trigger.
  const [skipped, setSkipped] = useState(false);

  // Trigger: tenant_admin role AND focus is null. Non-admins don't see the
  // modal because they can't write the focus column anyway.
  const shouldOpen =
    !skipped &&
    me.data &&
    me.data.focus === null &&
    (me.data.role === 'tenant_admin' || me.data.role === 'sys_admin');

  useEffect(() => {
    if (shouldOpen) setOpen(true);
  }, [shouldOpen]);

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      await apiFetch('/v1/me/focus', { method: 'PATCH', body: focus });
      // Phase D PR-12: trigger on-demand cold-start bootstrap so the AI
      // rail goes from cold → live in seconds, not at the next 06:00 UTC
      // CronJob run. Best-effort: a failure here doesn't block the modal
      // close (the nightly worker will catch up). Closes the demo-truth
      // gap from the post-impl review.
      apiFetch('/v1/personalize/bootstrap', { method: 'POST' })
        .then(() => qc.invalidateQueries({ queryKey: ['ai', 'suggest', 'horizon'] }))
        .catch(() => {
          /* best-effort; nightly worker will reconcile */
        });
      qc.invalidateQueries({ queryKey: ['me'] });
      setOpen(false);
    } catch (e) {
      setErr(e instanceof ApiError ? `${e.code}: ${e.detail ?? ''}`.trim() : String(e));
    } finally {
      setSaving(false);
    }
  }

  function skip() {
    setSkipped(true);
    setOpen(false);
  }

  if (!open) return null;

  return (
    <Drawer open={open} onClose={skip} title="WELCOME · TELL US WHAT YOU HUNT" wide>
      <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p className="meta mono" style={{ color: 'var(--fg-2)', lineHeight: 1.55, margin: 0 }}>
          The terminal personalises your AI rail. It learns your firm's interests from real activity
          (drafted bids, viewed briefs, applied screens) — but on day one we don't have that signal
          yet. Tell us what you target and we'll seed the rail with relevant opportunities
          immediately.
        </p>
        <p className="meta mono" style={{ color: 'var(--fg-3)', margin: 0 }}>
          You can change any of this later in Settings → Focus.
        </p>

        <TenantFocusForm value={focus} onChange={setFocus} />

        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="btn pri"
            style={{ minWidth: 120 }}
          >
            {saving ? 'saving…' : 'SAVE & CONTINUE'}
          </button>
          <button type="button" onClick={skip} className="btn gh">
            SKIP FOR NOW
          </button>
          {err && <span className="meta mono rd">{err}</span>}
        </div>
      </div>
    </Drawer>
  );
}
